// src/hooks/useSuspendedUsers.js
// Reads users/{uid} where status == "Inactive".
// Exposes: paged list, summary stats, client-side search, reinstate action.

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection, getDocs, query, where, updateDoc, doc, Timestamp,
} from 'firebase/firestore';
import { auth, db } from '../firebase';

export const SU_PAGE_SIZE = 25;

// ─── Parse any Firestore date ─────────────────────────────────────────────────
export const parseSuDate = (val) => {
  if (!val) return null;
  if (typeof val.toDate === 'function') return val.toDate();
  if (val instanceof Date) return val;
  if (val.seconds) return new Date(val.seconds * 1000);
  if (typeof val === 'string') { const d = new Date(val); return isNaN(d) ? null : d; }
  return null;
};

// ─── Map Firestore user doc → clean object ────────────────────────────────────
const mapUser = (snap) => {
  const d = snap.data();
  const fp = d.fraudProfile || {};
  return {
    _id: snap.id,
    uid: snap.id,                                      // uid IS the document ID
    name: d.name || d.displayName || '',
    email: d.email || '',
    phoneNumber: d.phoneNumber || d.phone || '',
    authProvider: d.authProvider || d.provider || '',
    status: d.status || '',
    createdAt: parseSuDate(d.createdAt),
    lastLogin: parseSuDate(d.lastLogin),
    isEmailVerified: d.isEmailVerified ?? false,
    // Fraud profile (nested)
    fraudScore: typeof fp.score === 'number' ? fp.score : null,
    highRiskPayments: fp.highRiskPayments ?? 0,
    lastPayment: parseSuDate(fp.lastPayment),
    lastCardFingerprint: fp.lastCardFingerprint || '',
    fraudProfileUpdatedAt: parseSuDate(fp.updatedAt),
    // Suspension fields
    suspendedAt: parseSuDate(d.suspendedAt),
    suspendedBy: d.suspendedBy || '',
    suspensionReason: d.suspensionReason || '',
    rawDoc: d,
  };
};

// ─── Main Hook ────────────────────────────────────────────────────────────────
const useSuspendedUsers = () => {
  const [allUsers, setAllUsers]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [opLoading, setOpLoading]     = useState(false);
  const [error, setError]             = useState(null);
  const [toast, setToast]             = useState(null);
  const [search, setSearch]           = useState('');
  const [page, setPage]               = useState(1);

  // Dialog state
  const [viewUser, setViewUser]             = useState(null);
  const [viewOpen, setViewOpen]             = useState(false);
  const [reinstateTarget, setReinstateTarget] = useState(null);
  const [reinstateOpen, setReinstateOpen]   = useState(false);
  const [blacklistTarget, setBlacklistTarget] = useState(null);
  const [blacklistOpen, setBlacklistOpen]   = useState(false);

  const fetchId = useRef(0);

  // ── Fetch suspended users ──────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    const id = ++fetchId.current;
    setLoading(true);
    setError(null);
    try {
      const q = query(collection(db, 'users'), where('status', '==', 'Inactive'));
      const snap = await getDocs(q);
      console.log(`[useSuspendedUsers] Fetched ${snap.size} Inactive user(s)`);
      if (id !== fetchId.current) return;
      const users = snap.docs.map(mapUser);
      // Sort by suspendedAt desc (most recently suspended first)
      users.sort((a, b) => {
        if (!a.suspendedAt && !b.suspendedAt) return 0;
        if (!a.suspendedAt) return 1;
        if (!b.suspendedAt) return -1;
        return b.suspendedAt - a.suspendedAt;
      });
      setAllUsers(users);
    } catch (err) {
      console.error('[useSuspendedUsers] Fetch error:', err);
      if (id === fetchId.current) setError(err.message);
    } finally {
      if (id === fetchId.current) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Toast ─────────────────────────────────────────────────────────────────
  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Summary stats ──────────────────────────────────────────────────────────
  const stats = (() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let highRisk = 0, scoreSum = 0, scoreCount = 0, suspendedToday = 0;
    allUsers.forEach((u) => {
      if (u.fraudScore !== null) { scoreSum += u.fraudScore; scoreCount++; }
      if (u.fraudScore !== null && u.fraudScore >= 70) highRisk++;
      if (u.suspendedAt && u.suspendedAt >= today) suspendedToday++;
    });
    const avgScore = scoreCount > 0 ? Math.round(scoreSum / scoreCount) : null;
    return { total: allUsers.length, avgScore, highRisk, suspendedToday };
  })();

  // ── Client-side search ─────────────────────────────────────────────────────
  const filtered = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return allUsers;
    return allUsers.filter((u) => {
      const hay = [u.uid, u.name, u.email, u.phoneNumber].join(' ').toLowerCase();
      return hay.includes(q);
    });
  })();

  const totalPages  = Math.max(1, Math.ceil(filtered.length / SU_PAGE_SIZE));
  const safePage    = Math.min(page, totalPages);
  const pageUsers   = filtered.slice((safePage - 1) * SU_PAGE_SIZE, safePage * SU_PAGE_SIZE);

  useEffect(() => { setPage(1); }, [search]);

  // ── Reinstate user ─────────────────────────────────────────────────────────
  const reinstateUser = useCallback(async (uid) => {
    setOpLoading(true);
    try {
      await updateDoc(doc(db, 'users', uid), {
        status: 'Active',
        suspendedAt: null,
        suspendedBy: null,
        suspensionReason: null,
        reinstatedAt: Timestamp.now(),
        reinstatedBy: auth.currentUser?.email || auth.currentUser?.uid || 'admin',
      });
      setAllUsers((prev) => prev.filter((u) => u.uid !== uid));
      setReinstateOpen(false);
      setReinstateTarget(null);
      showToast('success', 'User reinstated successfully');
    } catch (err) {
      showToast('error', `Failed to reinstate: ${err.message}`);
    } finally {
      setOpLoading(false);
    }
  }, []);

  // ── Dialog helpers ─────────────────────────────────────────────────────────
  const openView      = (u) => { setViewUser(u); setViewOpen(true); };
  const openReinstate = (u) => { setReinstateTarget(u); setReinstateOpen(true); };
  const openBlacklist = (u) => { setBlacklistTarget(u); setBlacklistOpen(true); };

  return {
    allUsers, pageUsers, filtered, stats,
    loading, opLoading, error, toast,
    search, setSearch,
    page: safePage, totalPages, setPage,
    hasNext: safePage < totalPages,
    hasPrev: safePage > 1,
    fetchAll,
    reinstateUser,
    // Dialogs
    viewUser, viewOpen, setViewOpen,
    reinstateTarget, reinstateOpen, setReinstateOpen,
    blacklistTarget, blacklistOpen, setBlacklistOpen,
    openView, openReinstate, openBlacklist,
  };
};

export default useSuspendedUsers;
